// frontend/admin-portal/src/components/sales/PaymentModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Alert, formatMoney } from "@fiberhome/shared-ui";
import { paymentService } from "../../services/paymentService";
import { treasuryService } from "../../services/treasuryService";

const METHODS = [
  "cash",
  "bank_transfer",
  "card",
  "cheque",
  "mobile_money",
  "credit_note",
  "other",
];

export default function PaymentModal({ open, invoice, onClose, onSaved }) {
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState(0);
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const balance = invoice
    ? Number(invoice.total) - Number(invoice.paidAmount || 0)
    : 0;

  useEffect(() => {
    if (!open || !invoice) return;
    setError("");
    setAmount(+balance.toFixed(2));
    setMethod("cash");
    setReference("");
    setNotes("");
    setPaidAt(new Date().toISOString().slice(0, 10));
    setAccountId("");
    treasuryService
      .dashboard()
      .then((d) => setAccounts(d.accounts || []))
      .catch(() => setAccounts([]));
  }, [open, invoice, balance]);

  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    try {
      await paymentService.create({
        invoiceId: invoice?.id,
        customerId: invoice?.customerId,
        method,
        amount: Number(amount),
        paidAt,
        reference,
        notes,
        treasuryAccountId: accountId ? Number(accountId) : undefined,
      });
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={invoice ? `Record Payment — ${invoice.code}` : "Record Payment"}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleSubmit} loading={saving}>
            Save Payment
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      {invoice && (
        <Alert variant="info" className="mb-3">
          Outstanding balance:{" "}
          <strong>{formatMoney(balance, { symbol: invoice.currency })}</strong>
        </Alert>
      )}

      <div className="row g-3">
        <div className="col-md-6">
          <label className="fh-input-label">Method</label>
          <select
            className="fh-input"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            {METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <Input
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="col-md-6">
          <Input
            label="Paid on"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
          />
        </div>
        <div className="col-md-6">
          <label className="fh-input-label">Credit Account (treasury)</label>
          <select
            className="fh-input"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">Default account</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.currency})
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <Input
            label="Reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
        <div className="col-12">
          <Input
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
